import {
  Component,
  computed,
  inject,
  input,
  linkedSignal,
  output,
  resource,
  signal,
} from '@angular/core';
import { TranslocoPipe } from '@jsverse/transloco';
import { HlmButtonImports } from '@mb/ui/button';
import { HlmButtonGroupImports } from '@mb/ui/button-group';
import { HlmFieldImports } from '@mb/ui/field';
import { PersonSearchListComponent } from '@modules/contacts/client';
import { PersonSearchDto } from '@modules/contacts/shared';
import { OrgChartApiService } from '../../services/org-chart-api.service';
import { DetailsShellComponent } from '@mb/components';
import type { OrgPersonPanelRequest } from './org-chart.types';

/** Assigns one or more persons to a position from the org-chart side sheet. */
@Component({
  selector: 'org-chart-person-edit-panel',
  standalone: true,
  imports: [
    HlmFieldImports,
    HlmButtonImports,
    HlmButtonGroupImports,
    PersonSearchListComponent,
    DetailsShellComponent,
    TranslocoPipe,
  ],
  template: `
    <app-details-shell>
      <h2 detailsTrail class="truncate font-medium text-foreground">
        {{ 'organigram.panel.title.assignPerson' | transloco }}
      </h2>

      <hlm-button-group detailsActions>
        <button
          type="button"
          hlmBtn
          variant="outline"
          (click)="cancelled.emit()"
        >
          {{ 'organigram.panel.actions.cancel' | transloco }}
        </button>
        <button
          type="button"
          hlmBtn
          [disabled]="saving() || !hasChanges()"
          (click)="save()"
        >
          {{
            (saving()
              ? 'organigram.panel.actions.saving'
              : 'organigram.panel.actions.save'
            ) | transloco
          }}
        </button>
      </hlm-button-group>

      <!-- The surface every other editing form in the sheet sits on. -->
      <div
        hlmFieldGroup
        class="rounded-md border border-border bg-form-surface p-3"
      >
        <div hlmField>
          <label hlmFieldLabel for="oc-person-search">{{
            'organigram.panel.fields.person' | transloco
          }}</label>

          <contacts-person-search-list
            inputId="oc-person-search"
            [persons]="persons()"
            [selectedIds]="selectedPersonIds()"
            [placeholder]="
              'organigram.panel.fields.personPlaceholder' | transloco
            "
            [emptyLabel]="'organigram.panel.noPersons' | transloco"
            (selectedIdsChange)="onSelectedIdsChange($event)"
          ></contacts-person-search-list>
        </div>
        @if (error(); as err) {
          <hlm-field-error [forceShow]="true">{{
            err | transloco
          }}</hlm-field-error>
        }
      </div>
    </app-details-shell>
  `,
})
export class OrgChartPersonEditPanelComponent {
  readonly #api = inject(OrgChartApiService);

  readonly request = input.required<OrgPersonPanelRequest>();
  readonly organizationId = input.required<string>();

  readonly saved = output<void>();
  readonly cancelled = output<void>();

  readonly saving = signal(false);
  readonly error = signal<string | null>(null);

  readonly selectedPersonIds = linkedSignal<string[]>(() =>
    this.request().assignees.map((assignee) => assignee.personId),
  );
  readonly isPrimary = linkedSignal<boolean>(() => {
    void this.request();
    return false;
  });

  readonly #initialAssigneeIds = computed(
    () => new Set(this.request().assignees.map((a) => a.personId)),
  );

  readonly personsResource = resource({
    params: () => ({
      organizationId: this.organizationId(),
    }),
    loader: async ({ params, abortSignal }) => {
      try {
        return await this.#api.listSearchPersons(
          { organizationId: params.organizationId, search: '' },
          { signal: abortSignal },
        );
      } catch {
        return [];
      }
    },
  });

  readonly persons = computed(
    (): readonly PersonSearchDto[] =>
      this.personsResource.value() ?? ([] as PersonSearchDto[]),
  );

  readonly hasChanges = computed(() => {
    const selected = new Set(this.selectedPersonIds());
    const initial = this.#initialAssigneeIds();
    if (selected.size !== initial.size) return true;
    for (const id of selected) {
      if (!initial.has(id)) return true;
    }
    return false;
  });

  onSelectedIdsChange(ids: readonly string[]): void {
    this.selectedPersonIds.set([...ids]);
  }

  async save(): Promise<void> {
    const personIds = this.selectedPersonIds();
    const initialIds = this.#initialAssigneeIds();
    const toAssign = personIds.filter((id) => !initialIds.has(id));
    const toUnassign = this.request().assignees.filter(
      (assignee) => !personIds.includes(assignee.personId),
    );

    if (toAssign.length === 0 && toUnassign.length === 0) {
      this.saved.emit();
      return;
    }

    this.saving.set(true);
    this.error.set(null);
    try {
      const positionId = this.request().positionId;
      const isPrimary = this.isPrimary();
      await Promise.all([
        ...toAssign.map((personId) =>
          this.#api.assignPerson({
            personId,
            positionId,
            isPrimary,
          }),
        ),
        ...toUnassign.map((assignee) =>
          this.#api.deletePersonPosition(assignee.personPositionId),
        ),
      ]);
      this.saved.emit();
    } catch {
      this.error.set('organigram.errors.saveFailed');
    } finally {
      this.saving.set(false);
    }
  }
}
